/**
 * Runtime parameters (spec §6, §7). Every value here is the default; the
 * live value comes from the `parameters` table and is served to clients at
 * GET /v1/parameters.
 */

export const DEFAULT_PARAMETERS = {
  // Geometry (informational — changing these would re-tile the canvas).
  pixel_level: 23,
  tile_level: 15,
  activity_level: 13,

  // Drawing
  draw_radius_m: 75,
  max_gps_accuracy_m: 50,
  max_ops_per_request: 200,
  max_tile_subscriptions: 64,
  max_speed_kmh: 300,

  // Ink
  ink_start: 50,
  ink_cap: 500,
  ink_accrual_seconds: 60,
  avatar_pixel_cost: 1,

  // Activity feed
  activity_delay_seconds: 600,
  activity_k_threshold: 3,
  /** 1 = agent paint counts toward the live activity feed (demos / seeding). */
  activity_include_agents: 0,

  // Notifications
  overwrite_notify_min: 10,
  collab_distance_px: 8,
  collab_window_hours: 24,

  // Badges
  founder_window_days: 30,
  crowd_min_others: 5,
  crowd_window_seconds: 600,
  restore_lookback_hours: 24,

  // Anti-abuse
  draw_rate_per_minute_user: 30,
  draw_rate_per_minute_ip: 90,
  reports_per_day_user: 20,
  report_dismissed_downweight_after: 3,

  // Moderation
  ink_freeze_hours: 24,
  suspension_days: 7,
  auto_action_confidence: 0.95,
  report_retention_days: 365,

  // Storage
  event_local_retention_days: 35,
  snapshot_hour_utc: 0,
} as const;

export type ParameterKey = keyof typeof DEFAULT_PARAMETERS;
export type Parameters = { -readonly [K in ParameterKey]: number };

export const PARAMETER_KEYS = Object.keys(DEFAULT_PARAMETERS) as ParameterKey[];

/** Keys whose values are safe to expose publicly (everything except abuse tuning). */
export const PUBLIC_PARAMETER_KEYS: ParameterKey[] = [
  'pixel_level',
  'tile_level',
  'activity_level',
  'draw_radius_m',
  'max_gps_accuracy_m',
  'max_ops_per_request',
  'max_tile_subscriptions',
  'ink_start',
  'ink_cap',
  'ink_accrual_seconds',
  'avatar_pixel_cost',
  'activity_delay_seconds',
  'activity_k_threshold',
  'overwrite_notify_min',
  'collab_distance_px',
  'founder_window_days',
];

export function isParameterKey(k: string): k is ParameterKey {
  return Object.prototype.hasOwnProperty.call(DEFAULT_PARAMETERS, k);
}

export function withDefaults(overrides: Partial<Record<string, number>>): Parameters {
  const out = { ...DEFAULT_PARAMETERS } as Parameters;
  for (const k of PARAMETER_KEYS) {
    const v = overrides[k];
    if (typeof v === 'number' && Number.isFinite(v)) out[k] = v;
  }
  return out;
}

/**
 * Ink cost of painting `pixels` on a tile whose pixels cover `areaFactor` of the
 * ground a pixel covers at the equator.
 *
 * The canvas grid is Web Mercator, so a pixel is a constant patch of screen and
 * a shrinking patch of ground as you go north: 1.19 m at the equator, 0.75 m in
 * London, 0.41 m at 70 degrees. Charging by ground area means covering a given
 * piece of the world costs the same wherever you stand. Any paint at all costs
 * at least one, so nothing is ever free.
 */
export function inkForPixels(pixels: number, areaFactor: number): number {
  if (pixels <= 0) return 0;
  return Math.max(1, Math.round(pixels * areaFactor));
}
